import {IPriceBand, IPriceBandSnap} from '../../../shared/shared-models/price-banding/price-band';
import {IRange} from '../../../shared/shared-models/range/range';
import {getDeepCopyOfObject} from '../../../shared/shared-utils/object/object.utils';
import {snapPriceToPriceBands} from '../../../shared/shared-utils/price/price-banding.utils';

/**
 * Builds an empty price-band rule with a starter 0–100 range.
 */
export function createEmptyPriceBand(): IPriceBand {
  return {
    range: {
      start: {v: 0, inc: true},
      end: {v: 100, inc: false},
    },
    centSnaps: [],
  };
}

/**
 * Builds an empty snap row for rand or cent rounding.
 */
export function createEmptyPriceBandSnap(maxValue: number): IPriceBandSnap {
  return {
    range: {
      start: {v: 0, inc: true},
      end: {v: maxValue, inc: false},
    },
    v: maxValue,
  };
}

/**
 * Formats a money amount for settings copy, e.g. R19.47.
 */
export function formatPriceBandMoney(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) {
    return 'any price';
  }
  return `R${value.toFixed(2)}`;
}

/**
 * Formats a range as plain language, including inclusive/exclusive edges.
 */
export function describePriceRange(range: IRange | undefined, unit: string = ''): string {
  if (!range?.start && !range?.end) {
    return 'any value';
  }
  const startLabel: string = range.start
    ? `${range.start.inc === false ? 'above ' : ''}${range.start.v}${unit}`
    : 'any';
  const endLabel: string = range.end
    ? `${range.end.inc === false ? 'up to but not including ' : ''}${range.end.v}${unit}`
    : 'any';
  if (range.start && !range.end) {
    return `${range.start.inc === false ? 'greater than' : 'from'} ${range.start.v}${unit}`;
  }
  if (!range.start && range.end) {
    return `${range.end.inc === false ? 'less than' : 'up to'} ${range.end.v}${unit}`;
  }
  return `${startLabel} to ${endLabel}`;
}

/**
 * Builds a short collapsed-header range label for a price-band rule.
 */
export function describePriceBandRange(band: IPriceBand): string {
  const start: number | undefined = band.range?.start?.v;
  const end: number | undefined = band.range?.end?.v;
  if (start === undefined && end === undefined) {
    return 'Any price';
  }
  if (start !== undefined && end === undefined) {
    return `From ${formatPriceBandMoney(start)}`;
  }
  if (start === undefined && end !== undefined) {
    return `Up to ${formatPriceBandMoney(end)}`;
  }
  return `${formatPriceBandMoney(start)} – ${formatPriceBandMoney(end)}`;
}

/**
 * Builds a one-line summary of what a price-band rule does.
 */
export function describePriceBand(band: IPriceBand): string {
  const rangeText: string = describePriceRange(band.range);
  const parts: string[] = [`Prices ${rangeText}`];

  if (band.randSnap?.snaps?.length) {
    const digits: number = band.randSnap.digits ?? 1;
    const snaps: string = band.randSnap.snaps
      .map((snap: IPriceBandSnap): string => `${describePriceRange(snap.range)} → ${snap.v}`)
      .join('; ');
    parts.push(`last ${digits} rand digit${digits === 1 ? '' : 's'} ${snaps}`);
  }

  if (band.centSnaps?.length) {
    const snaps: string = band.centSnaps
      .map((snap: IPriceBandSnap): string => `${describePriceRange(snap.range, 'c')} → ${snap.v}c`)
      .join('; ');
    parts.push(`cents ${snaps}`);
  }

  if (!band.randSnap?.snaps?.length && !band.centSnaps?.length) {
    parts.push('no rounding yet');
  }

  return parts.join('. ');
}

/**
 * Builds a few example prices inside a band so users can see the snap result.
 */
export function buildPriceBandPreviewExamples(band: IPriceBand): {from: number; to: number}[] {
  const start: number = band.range?.start?.v ?? 0;
  const end: number = band.range?.end?.v ?? start + 10;
  const span: number = Math.max(end - start, 1);
  const candidates: number[] = [
    convertToExamplePrice(start + 0.47),
    convertToExamplePrice(start + span * 0.35 + 0.13),
    convertToExamplePrice(end > start ? end - 0.01 : start + 0.99),
  ];

  const unique: number[] = [];
  candidates.forEach((price: number): void => {
    if (price > 0 && !unique.includes(price)) {
      unique.push(Number(price.toFixed(2)));
    }
  });

  return unique.slice(0, 3).map((price: number): {from: number; to: number} => ({
    from: price,
    to: Number(snapPriceToPriceBands(price, [band]).toFixed(2)),
  }));
}

/**
 * Validates a draft list of price bands and returns field-level messages.
 */
export function validatePriceBands(bands: IPriceBand[]): {error: string; field: string}[] {
  const errors: {error: string; field: string}[] = [];

  bands.forEach((band: IPriceBand, index: number): void => {
    const start: number | undefined = band.range?.start?.v;
    const end: number | undefined = band.range?.end?.v;
    if (start !== undefined && end !== undefined && start >= end) {
      errors.push({
        field: `rule-${index}-range`,
        error: `Rule ${index + 1}: the "from" price must be lower than the "to" price.`,
      });
    }

    band.centSnaps?.forEach((snap: IPriceBandSnap, snapIndex: number): void => {
      if (!isSnapInBounds(snap, 99)) {
        errors.push({
          field: `rule-${index}-cent-${snapIndex}`,
          error: `Rule ${index + 1} cent snap ${snapIndex + 1} must stay between 0 and 99 cents.`,
        });
      }
    });

    const maxRand: number = Math.pow(10, band.randSnap?.digits ?? 1) - 1;
    band.randSnap?.snaps?.forEach((snap: IPriceBandSnap, snapIndex: number): void => {
      if (!isSnapInBounds(snap, maxRand)) {
        errors.push({
          field: `rule-${index}-rand-${snapIndex}`,
          error: `Rule ${index + 1} rand snap ${snapIndex + 1} must stay between 0 and ${maxRand}.`,
        });
      }
    });
  });

  return errors;
}

/**
 * Returns true when two price-band ranges share any price.
 */
export function doPriceBandRangesOverlap(left: IPriceBand, right: IPriceBand): boolean {
  const leftRange: IRange | undefined = left.range;
  const rightRange: IRange | undefined = right.range;
  if (!leftRange || !rightRange) {
    return false;
  }

  const leftStart: number = leftRange.start?.v ?? Number.NEGATIVE_INFINITY;
  const leftEnd: number = leftRange.end?.v ?? Number.POSITIVE_INFINITY;
  const rightStart: number = rightRange.start?.v ?? Number.NEGATIVE_INFINITY;
  const rightEnd: number = rightRange.end?.v ?? Number.POSITIVE_INFINITY;

  if (leftEnd < rightStart || rightEnd < leftStart) {
    return false;
  }
  if (leftEnd === rightStart) {
    return leftRange.end?.inc !== false && rightRange.start?.inc !== false;
  }
  if (rightEnd === leftStart) {
    return rightRange.end?.inc !== false && leftRange.start?.inc !== false;
  }
  return true;
}

/**
 * Returns overlap warnings keyed by the later rule index.
 */
export function getPriceBandOverlapWarnings(bands: IPriceBand[]): Record<number, string> {
  const warnings: Record<number, string> = {};
  bands.forEach((band: IPriceBand, index: number): void => {
    for (let earlier: number = 0; earlier < index; earlier += 1) {
      if (doPriceBandRangesOverlap(bands[earlier], band)) {
        warnings[index] = `This range overlaps Rule ${earlier + 1}. Rule ${earlier + 1} will be used for prices that match both.`;
        return;
      }
    }
  });
  return warnings;
}

/**
 * Returns a deep-cloned list so draft edits never mutate store state.
 */
export function clonePriceBands(bands: IPriceBand[] | undefined): IPriceBand[] {
  return getDeepCopyOfObject(bands ?? []) ?? [];
}

function convertToExamplePrice(value: number): number {
  return Number(Math.max(value, 0.01).toFixed(2));
}

function isSnapInBounds(snap: IPriceBandSnap, maxValue: number): boolean {
  const start: number = snap.range?.start?.v ?? 0;
  const end: number = snap.range?.end?.v ?? maxValue;
  const target: number = snap.v ?? 0;
  return [start, end, target].every((value: number): boolean => value >= 0 && value <= maxValue && start <= end);
}
